'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { cva } from 'class-variance-authority'
import type { AriaSwitchProps } from 'react-aria'
import { Switch as AriaSwitch } from 'react-aria-components'
import { Knob } from './_components/Knob.client'

const toggleVariants = cva(
  [
    'group/toggle relative flex cursor-pointer select-none items-center',
    'rounded-full p-[0.125rem]',
    'transition-all duration-100',
    'outline-2 outline-offset-2 outline-brand-1-700',
    'bg-brand-2-400',
    'data-[selected]:bg-brand-2-800',
    'data-[pressed]:bg-brand-2-500 data-[pressed]:data-[selected]:bg-brand-2-500',
    'data-[hovered]:bg-brand-2-200 data-[hovered]:data-[selected]:bg-brand-2-900',
    'data-[disabled]:cursor-not-allowed data-[disabled]:bg-brand-2-200 data-[disabled]:data-[selected]:bg-brand-2-100',
    'data-[loading]:pointer-events-none data-[loading]:cursor-wait',
    'data-[focus-visible]:outline',
  ],
  {
    variants: {
      variant: {
        lg: 'h-3 w-6',
        sm: 'h-2 w-4',
      },
      isClickAndCollect: {
        true: twMerge(
          'data-[selected]:bg-click-and-collect-fg data-[selected]:data-[hovered]:bg-click-and-collect-fg',
          'data-[disabled]:data-[selected]:bg-click-and-collect-disabled',
        ),
      },
    },
    defaultVariants: {
      variant: 'lg',
      isClickAndCollect: false,
    },
  },
)

export type ToggleProps = {
  isLoading?: boolean
  isClickAndCollect?: boolean
  variant?: 'lg' | 'sm'
} & AriaSwitchProps &
  UiComponentProps &
  (
    | {
        'aria-label': string
        'aria-labelledby'?: never
      }
    | {
        'aria-label'?: never
        'aria-labelledby': string
      }
  )

function Toggle({ isLoading, isClickAndCollect, variant = 'lg', ...props }: ToggleProps) {
  return (
    <AriaSwitch
      {...props}
      data-testid="toggle"
      data-loading={isLoading || undefined}
      className={toggleVariants({
        variant,
        isClickAndCollect,
      })}
    >
      {(renderProps) => (
        <Knob {...renderProps} isLoading={isLoading} isClickAndCollect={isClickAndCollect} />
      )}
    </AriaSwitch>
  )
}

export function ToggleSkeleton({
  variant = 'lg',
  ...props
}: { variant?: 'lg' | 'sm' } & UiComponentProps) {
  return (
    <BaseSkeleton
      {...props}
      data-testid="toggle-skeleton"
      className={twMerge('rounded-full', variant === 'lg' ? 'h-3 w-6' : 'h-2 w-4')}
    />
  )
}

Toggle.Skeleton = ToggleSkeleton

export { Toggle }
